import { Dispatch, Fragment, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';

import { flexRender, Row, Table } from '@tanstack/react-table';

import { CountBadge } from '../count-badge';
import { Icon } from '../icon';
import { SortIcon } from './sort-icon';
import { TablePagination } from './table-pagination';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from '@/new/components/shadcn/empty';
import { Popover, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/new/components/shadcn/table';
import { cn } from '@/new/lib/utils';

export function ScrollableTable<T>({
  table,
  globalFilter,
  setGlobalFilter,
  filters,
  plural,
  rowOnClick,
  additionalButtons,
  className,
  searchDataTour,
  tableDataTour,
  loading,
  emptyState,
}: {
  table: Table<T>;
  globalFilter: any;
  setGlobalFilter: Dispatch<SetStateAction<any>>;
  filters?: {
    popover: ReactNode;
    count?: number;
    clearAll: () => void;
  };
  plural: string;
  rowOnClick?: (row: Row<T>) => void;
  additionalButtons?: ReactNode;
  className?: string;
  searchDataTour?: string;
  tableDataTour?: string;
  loading?: boolean;
  emptyState?: ReactNode;
}) {
  // For applying the fade effect
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasInitializedAsyncContent, setHasInitializedAsyncContent] = useState(loading === undefined || !loading);

  // Check if scrolled to bottom when data changes and on mount
  useEffect(() => {
    const target = scrollContainerRef.current;
    if (target) {
      setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, [table.getRowCount()]);

  const hasRows = table.getRowModel().rows.length > 0;
  const hasSearch = typeof globalFilter === 'string' ? globalFilter.trim().length > 0 : Boolean(globalFilter);
  const shouldShowCustomEmptyState = Boolean(emptyState) && !hasSearch;
  const shouldShowEmptyState = !loading && !hasRows && hasInitializedAsyncContent;
  const resolvedEmptyState = shouldShowCustomEmptyState ? (
    emptyState
  ) : (
    <Empty className="my-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon className="lucide--inbox" />
        </EmptyMedia>
        <EmptyTitle>No {plural} found</EmptyTitle>
        <EmptyDescription>
          {globalFilter || (filters?.count ?? 0) > 0
            ? `No ${plural} match your current filters. Try adjusting your search or filters.`
            : `There are no ${plural} to display.`}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );

  useEffect(() => {
    if (loading === false || hasRows) {
      setHasInitializedAsyncContent(true);
    }
  }, [loading, hasRows]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  const hasFilters = Boolean(filters);

  return (
    <section className={cn('-mt-1 flex grow flex-col overflow-hidden px-8 py-1', className)}>
      <div className="my-1 flex w-full items-center gap-2" data-tour={searchDataTour}>
        <SearchInput
          placeholder={`Search ${plural}...`}
          className={cn(hasFilters || additionalButtons ? 'grow' : 'w-full max-w-md')}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />

        {filters && (
          <ButtonGroup>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="relative">
                  <Icon className="lucide--funnel-plus" />
                  <span>Add Filters</span>
                  {filters.count !== undefined && <CountBadge count={filters.count} />}
                </Button>
              </PopoverTrigger>

              {filters.popover}
            </Popover>

            <Button onClick={filters.clearAll}>
              <Icon className="lucide--funnel-x" />
              <span>Clear Filters</span>
            </Button>
          </ButtonGroup>
        )}

        {additionalButtons && additionalButtons}
      </div>

      <div
        className="mt-2 grow overflow-auto mask-b-from-80%"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{ maskSize: isScrolledToBottom ? '100% 120%' : '100% 100%' }}
        data-tour={tableDataTour}
      >
        <TableComponent>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div
                      className="flex cursor-pointer items-center gap-2 select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon column={header.column} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {loading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {table.getHeaderGroups()[0]?.headers.map((header) => (
                      <TableCell key={header.id}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : table.getRowModel().rows.map((row, index, rows) => {
                  const meta = table.options.meta as any;
                  const rowClassName = meta?.getRowClassName?.(row.original);
                  const getGroupKey = meta?.getGroupKey;
                  const formatGroupLabel = meta?.formatGroupLabel;
                  const renderGroupActions = meta?.renderGroupActions;
                  const isGrouped = !!getGroupKey && !!formatGroupLabel;
                  const showGroupHeader =
                    isGrouped && (index === 0 || getGroupKey(row.original) !== getGroupKey(rows[index - 1].original));
                  const groupKey = getGroupKey?.(row.original);
                  const groupedRows = table.getPrePaginationRowModel().rows;
                  const groupRows = showGroupHeader
                    ? groupedRows.filter((r) => getGroupKey(r.original) === groupKey)
                    : [];
                  const groupCount = groupRows.length;
                  const selectedInGroup = groupRows.filter((r) => r.getIsSelected()).length;
                  const allGroupSelected = groupCount > 0 && selectedInGroup === groupCount;
                  const someGroupSelected = selectedInGroup > 0 && !allGroupSelected;

                  return (
                    <Fragment key={row.id}>
                      {showGroupHeader && (
                        <TableRow className="bg-gray-50/80 hover:bg-gray-50/80">
                          <TableCell
                            colSpan={row.getVisibleCells().length}
                            className="py-1.5 text-xs font-medium text-gray-500"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 px-0.5">
                                <Checkbox
                                  checked={allGroupSelected ? true : someGroupSelected ? 'indeterminate' : false}
                                  onCheckedChange={(checked) => {
                                    groupRows.forEach((r) => r.toggleSelected(!!checked));
                                  }}
                                  className="mx-2.75"
                                />
                                {formatGroupLabel(row.original)}
                                <span className="text-gray-400">({groupCount})</span>
                              </div>
                              {renderGroupActions?.(row.original, groupRows)}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow
                        className={cn(rowOnClick ? 'cursor-pointer' : '', rowClassName)}
                        onClick={rowOnClick ? () => rowOnClick(row) : undefined}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <TableCell key={cell.id} className={cn(isGrouped && cellIndex === 1 && 'pl-6')}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    </Fragment>
                  );
                })}
          </TableBody>
        </TableComponent>

        {shouldShowEmptyState && resolvedEmptyState}
      </div>

      <TablePagination className="mb-4" table={table} plural={plural} />
    </section>
  );
}
